import { useMemo } from "react";
import { SmallTitleSubRow, SmallTitleWithSubSpan, SummaryChip } from "src/components/common/common";
import { useHRM3050_R01Store } from "./Store_HRM3050_R01";
import { formatHRM3050_R01Hours, getHRM3050_R01ShiftSummary } from "./Util_HRM3050_R01";

/**
 * 선택 근무조의 정시 기준 시간 요약(정시 체류, 근무제외 휴게, 1일 소정근로, 지각·조퇴·외출에서 빼는 휴게)을 표시한다.
 * 값은 저장하지 않고 화면에서만 계산한다. 편집 중인 근무조·휴게 값이 바로 반영된다.
 * 실행 샘플의 휴게 타임라인 그래프는 공통 컴포넌트가 없어 넣지 않고 숫자 요약만 보여 준다.
 * @returns 휴게 영역 제목과 시간 요약 JSX. 선택 근무조가 없으면 제목만 표시한다.
 */
export const Summary_HRM3050_R01 = () => {
    // 화면 Store에서 선택 근무조, 휴게 목록, 휴게 삭제 표시 목록을 가져온다.
    const { selectedHRM3050_R01, HRM3050_R01DetailList, deletedHRM3050_R01DetailList } = useHRM3050_R01Store();

    // 삭제 표시를 뺀 휴게로 계산한 요약. 정시 출퇴근 형식이 맞지 않으면 null이다.
    // 의존성: 선택 근무조(편집 값 포함), 휴게 목록, 삭제 표시가 바뀔 때만 다시 계산한다.
    const summary = useMemo(() => {
        // 선택 근무조가 없으면 계산할 대상이 없다.
        if (!selectedHRM3050_R01) return null;
        const deletedSet = new Set(deletedHRM3050_R01DetailList.map(String));
        return getHRM3050_R01ShiftSummary(
            selectedHRM3050_R01,
            HRM3050_R01DetailList.filter(row => !deletedSet.has(String(row.valueIndex))),
        );
    }, [selectedHRM3050_R01, HRM3050_R01DetailList, deletedHRM3050_R01DetailList]);

    // 요약 항목 목록. 표시명, 값, 강조 여부(1일 소정근로)를 모아 둔다.
    // 의존성: 요약 값이 바뀔 때만 다시 만든다.
    const summaryItemList = useMemo(() => summary ? [
        { label: '정시 체류', value: formatHRM3050_R01Hours(summary.stayMinutes), isStrong: false },
        { label: '근무제외 휴게', value: `${summary.excludedBreakMinutes}분`, isStrong: false },
        { label: '1일 소정근로', value: formatHRM3050_R01Hours(summary.workMinutes), isStrong: true },
        { label: '지각·조퇴·외출에서 빼는 휴게', value: `${summary.allBreakMinutes}분`, isStrong: false },
    ] : [], [summary]);

    return (
        <div className="flex flex-col">
            {/* 휴게시간 제목 오른쪽에 선택 근무조가 있을 때만 코드·이름·출퇴근 시각을 표시한다. */}
            <SmallTitleWithSubSpan title="휴게시간" subTitle={selectedHRM3050_R01
                ? `${selectedHRM3050_R01.shiftCd} ${selectedHRM3050_R01.shiftNm} (${selectedHRM3050_R01.startTime}~${selectedHRM3050_R01.endTime})`
                : ''} />
            {/* 공통 보조 줄로 제목·요약·그리드 테두리를 연결하며, 유효한 선택 근무조가 있을 때만 요약을 표시한다. */}
            {summary && (
                <SmallTitleSubRow className="gap-1.5 text-xs">
                    {summaryItemList.map(item => (
                        // 일일근태와 같은 공통 요약 칩을 사용하고, 1일 소정근로가 8시간을 넘을 때만 빨간색으로 표시한다.
                        <SummaryChip key={item.label} label={item.label} value={item.value}
                            tone={item.isStrong && summary.workMinutes > 480 ? 'red' : 'default'} />
                    ))}
                    <span className="text-gray-500">
                        근무제외 N(10분 휴식 등)은 근무로 인정하되, 그 시간에 지각·외출해도 지각·외출 시간으로 세지 않습니다.
                    </span>
                </SmallTitleSubRow>
            )}
        </div>
    );
};
